import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import { MapContainer, ImageOverlay } from 'react-leaflet';
import L from 'leaflet';
import { TileLayer } from 'react-leaflet';



const AnalyticsProductNDVISentinel2 = () => {
  const history = useHistory();
  const [activeLevel, setActiveLevel] = useState('L2A');
  const [mapType, setMapType] = useState('satellite'); // 'street' or 'satellite'
  const bounds = [
    [12.54, 77.75], // SouthWest corner (min lat, min lng)
    [13.56, 78.79]  // NorthEast corner (max lat, max lng)
  ];
  
  // Define processing level information based on selected level
  const levelInfo = {
    'L0': {
      radiometricLevel: 'Raw Instrument Data',
      geometricProcessing: 'None',
      description: 'Level-0 products contain raw, unprocessed telemetry data from the Sentinel-2 sensor. These are used internally by ground stations for further processing and are not distributed to end users.',
    },
    'L1A': {
      radiometricLevel: 'Unprocessed Image Data',
      geometricProcessing: 'None',
      description: 'Level-1A products consist of reconstructed, unprocessed image data at full instrument resolution, with appended radiometric and geometric metadata. These are internal and not made available to users.',
    },
    'L1B': {
      radiometricLevel: 'Radiometrically Corrected',
      geometricProcessing: 'Sensor Corrections Applied',
      description: 'Level-1B products are radiometrically corrected (converted to radiance), with sensor-related geometric distortions removed. However, they are not yet georeferenced. This level is part of internal processing only.',
    },
    'L1C': {
      radiometricLevel: 'Top-of-Atmosphere Reflectance',
      geometricProcessing: 'Orthorectified (Map Projection Applied)',
      description: 'Level-1C products are the first user-accessible data level. They provide Top-of-Atmosphere (TOA) reflectance, are orthorectified using a Digital Elevation Model (DEM), and are georeferenced in a standard map projection. Suitable for visualization and basic analysis.',
    },
    'L2A': {
      radiometricLevel: 'Bottom-of-Atmosphere (Surface Reflectance)',
      geometricProcessing: 'Orthorectified',
      description: 'Level-2A products are atmospherically corrected from L1C to provide surface reflectance (BOA). They include scene classification and cloud/shadow masks. Ideal for scientific analysis, vegetation indices, and land cover classification.',
    }
  };
  

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      {/* Back Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#4287f5',
            textTransform: 'none',
            fontWeight: 500
          }}
          onClick={() => history.push('/products')}
        >
          Products &gt; NDVI Sentinel - 2
        </Button>
      </Box>

      {/* Product Description */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Product Description
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
        The Normalized Difference Vegetation Index (NDVI) is a remote sensing technique widely used to assess the health, density, and distribution of vegetation across landscapes. NDVI is calculated by comparing the reflectance of near-infrared (NIR) and visible red light, providing insights into plant photosynthetic activity and biomass production. Healthy vegetation absorbs more visible light for photosynthesis and reflects more NIR light, resulting in higher NDVI values.NDVI data products offer a range of applications in agriculture, forestry, ecology, and climate studies. They aid in monitoring crop health, detecting vegetation stress, assessing land cover changes, and quantifying carbon sequestration in forests. NDVI maps also contribute to habitat assessment, biodiversity monitoring, and ecosystem resilience analysis.Furthermore, NDVI is crucial in drought monitoring, desertification studies, and land degradation assessment. By analyzing NDVI trends over time, researchers can evaluate the impact of environmental factors on vegetation dynamics, predict crop yields, and inform land management decisions for sustainable resource use. NDVI data products are essential tools for understanding and managing terrestrial ecosystems worldwide.
Unscaled version NDVI will have values -1 to +1 and scaled version will have values 0 to 200.
      </Typography>
      </Box>

      {/* Specifications Table */}
      <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid #d0d7de', boxShadow: 'none' }}>
  <Table>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Geographical Coverage</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Image Resolution</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Source</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Revisit Frequency</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Data Format</TableCell>
        {/* <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Unit</TableCell> */}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow hover>
        <TableCell align="center">Global*</TableCell>
        <TableCell align="center">10 m x 10 m Pixel</TableCell>
        <TableCell align="center">Sentinel - 2</TableCell>
        <TableCell align="center">5-day cycle</TableCell>
        <TableCell align="center">GeoTIFF, .png, .csv</TableCell>
        {/* <TableCell align="center">N/A</TableCell> */}
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'gray', py: 1 }}>
          *Global coverage available. AOI outside India requires additional processing time.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>

  <Table sx={{ mt: 4 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Spectral Band</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Wavelength Range</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
  <TableRow hover>
    <TableCell>Band 1 (Scaled)</TableCell>
    <TableCell>0 to 200</TableCell>
    <TableCell>When the values are closer to 200 that indicate a healthier vegetation
    while values which are closer to 50 indicate an unhealthier vegetation.</TableCell>
  </TableRow>
</TableBody>
  </Table>
</TableContainer>
<Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
Sample Preview
        </Typography>

     {/* Simple Leaflet Map with ImageOverlay */}
<Box sx={{ height: 500, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
  <MapContainer
    bounds={bounds}
    style={{ height: '100%', width: '100%' }}
    scrollWheelZoom={false}
    zoomControl={false}
    attributionControl={false}
  >
    <TileLayer
  attribution='&copy; OpenStreetMap contributors'
  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
/>
    <ImageOverlay
      bounds={bounds}
      url="https://i.ibb.co/8g8nphvy/ndvi-fixed-range-output.png"
      opacity={1}
    />
  </MapContainer>
</Box>




      {/* Order Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            py: 1,
            px: 4,
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Order Imagery
        </Button>
      </Box>
    </Box>
  );
};

export default AnalyticsProductNDVISentinel2;